import React, { useCallback, useEffect } from 'react';

import delv from 'dlv';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import RewardsUnavailable from 'components/app-header/loyalty/rewards-unavailable';
import { NavigationAwareBackArrow } from 'components/navigation/back-arrow';
import { Tab, TabPanel, Tabs } from 'components/tabs';
import { useSetLoyaltyCardRewardsTierMutation } from 'generated/rbi-graphql';
import { withAuthRequired } from 'pages/authentication/auth-required';
import { useCustomizableContent } from 'state/customizable-content';

import Details from './details';
import RewardCategories from './reward-categories';
import TransactionHistory from './transaction-history';

const TabContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  background-color: ${Styles.color.primary};

  button {
    font-weight: ${Styles.fontWeight.heavy};
  }

  ${Styles.desktop`
    margin-top: 6.9rem;
  `};
`;

const TabInner = styled.div`
  display: flex;
  width: 400px;
`;

export const Background = styled.div`
  background: linear-gradient(
    ${Styles.color.primary} 0 243px,
    ${Styles.color.background} 243px 100%
  );

  ${Styles.mobileTiny`
    background: linear-gradient(
      ${Styles.color.primary} 0 230px,
      ${Styles.color.background} 230px 100%
    );
  `}

  ${Styles.desktop`
    background: linear-gradient(${Styles.color.primary} 0 272px, ${Styles.color.background} 272px 100%);
  `}
`;

const TabContentContainer = styled.div`
  width: 90%;
  margin: 0 auto;
  padding-bottom: 40px;
  max-width: 660px;
  min-height: calc(100vh - 107px);

  ${Styles.desktop`
    padding-bottom: 60px;
  `}
`;

const TabsMapping = {
  details: 0,
  redeem: 1,
  history: 2,
};

const TimsRewards = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { formatMessage } = useIntl();
  const [setLoyaltyCardRewardsTier] = useSetLoyaltyCardRewardsTierMutation();

  const currentTab = TabsMapping[tab] || 0;

  const {
    requestLoyaltyCustomizableContent,
    isLoadingLoyalty,
    loyaltyContent,
  } = useCustomizableContent();

  useEffect(requestLoyaltyCustomizableContent, [requestLoyaltyCustomizableContent]);

  function a11yProps(index: number) {
    return {
      id: `loyalty-tab-${index}`,
      ariaControls: `loyalty-tabpanel-${index}`,
    };
  }

  const handleTabChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      event.preventDefault();
      const newTab = Object.keys(TabsMapping).find(key => TabsMapping[key] === newValue);
      navigate(`${formatMessage({ id: 'routes.timsRewards' })}/${newTab}`);
    },
    [formatMessage, navigate]
  );

  if (isLoadingLoyalty) {
    return null;
  }

  return (
    <>
      <Helmet title={formatMessage(messages.thTimsRewards)} />
      <NavigationAwareBackArrow color={Styles.color.headerContrast} />
      <TabContainer>
        <TabInner>
          <Tabs
            selectedIndex={currentTab}
            onChange={handleTabChange}
            color="inverted"
            slidingUnderline
          >
            <Tab
              label={delv(loyaltyContent, 'detailsTabName.locale')}
              {...a11yProps(0)}
              value={0}
            />
            <Tab
              label={delv(loyaltyContent, 'rewardSetupTabName.locale')}
              {...a11yProps(1)}
              value={1}
            />
            <Tab
              label={delv(loyaltyContent, 'historyTabName.locale')}
              {...a11yProps(2)}
              value={2}
            />
          </Tabs>
        </TabInner>
      </TabContainer>
      <TabPanel
        value={currentTab}
        index={0}
        id={`loyalty-tabpanel-${0}`}
        ariaLabeledBy={`loyalty-tab-${0}`}
      >
        <Details loyaltyContent={loyaltyContent} />
      </TabPanel>
      <TabPanel
        value={currentTab}
        index={1}
        id={`loyalty-tabpanel-${1}`}
        ariaLabeledBy={`loyalty-tab-${1}`}
      >
        <RewardsUnavailable />
        <Background>
          <TabContentContainer>
            <RewardCategories
              radioButtonName="rewards-page-redeem-reward-tier"
              setSelectedRewardsTier={setLoyaltyCardRewardsTier}
            />
          </TabContentContainer>
        </Background>
      </TabPanel>
      <TabPanel
        value={currentTab}
        index={2}
        id={`loyalty-tabpanel-${2}`}
        ariaLabeledBy={`loyalty-tab-${2}`}
      >
        <Background>
          <TabContentContainer>
            <TransactionHistory />
          </TabContentContainer>
        </Background>
      </TabPanel>
    </>
  );
};

export default withAuthRequired(TimsRewards);
const messages = defineMessages({
  thTimsRewards: {
    id: 'thTimsRewards',
    defaultMessage: 'Tims Rewards',
    description: 'Header text for tim rewards section',
  },
});
